import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home,
  Building2,
  Factory,
  Snowflake,
  Package,
  Container,
  Truck,
  ShieldCheck,
  Target,
  Eye,
} from "lucide-react";
import { fileUrl, trackConversion } from "../../lib/api";
import { usePrefs } from "../../context/PrefsContext";

const HERO_DEFAULT =
  "https://images.unsplash.com/photo-1760227012914-88b62aafa928?crop=entropy&cs=srgb&fm=jpg&q=85";
const ABOUT_DEFAULT =
  "https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&q=85";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

export const Hero = ({ hero, image, video, posY }) => (
  <section id="utama" data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
    {video ? (
      <video
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: `center ${posY ?? 50}%` }}
        src={fileUrl(video)}
        autoPlay
        muted
        loop
        playsInline
        data-testid="hero-video"
      />
    ) : (
      <div
        className="absolute inset-0 bg-cover"
        style={{ backgroundImage: `url(${fileUrl(image) || HERO_DEFAULT})`, backgroundPosition: `center ${posY ?? 50}%` }}
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/80 to-slate-950/40" />
    <div className="relative max-w-7xl mx-auto px-6 pt-36 pb-20 w-full">
      <motion.div {...fadeUp} className="max-w-3xl">
        <span className="inline-block text-orange-500 border border-orange-500/40 bg-orange-500/10 rounded-full px-4 py-1.5 text-xs tracking-widest uppercase font-semibold mb-6">
          {hero.badge}
        </span>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.08] mb-6">
          {hero.title}
        </h1>
        <p className="text-slate-300 text-base md:text-lg max-w-xl mb-10">{hero.subtitle}</p>
        <div className="flex flex-wrap gap-4">
          <Link
            to="/hubungi"
            data-testid="hero-cta-primary"
            className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
          >
            {hero.cta_primary}
          </Link>
          <Link
            to="/tentang-kami"
            data-testid="hero-cta-secondary"
            className="border border-white/25 hover:border-orange-500 text-white font-semibold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
          >
            {hero.cta_secondary}
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

export const About = ({ about, image }) => {
  const { t } = usePrefs();
  return (
    <section id="tentang" data-testid="about-section" className="py-24 lg:py-32 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div {...fadeUp}>
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.about.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3 mb-6">{about.title}</h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-10">{about.text}</p>
          <div className="space-y-6">
            <div className="flex gap-4 p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg shadow-sm dark:shadow-none hover:border-orange-500/50 transition-colors">
              <Target className="text-orange-600 dark:text-orange-500 shrink-0 mt-1" size={22} />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 dark:text-white mb-1">{t.about.mission}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{about.mission}</p>
              </div>
            </div>
            <div className="flex gap-4 p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg shadow-sm dark:shadow-none hover:border-orange-500/50 transition-colors">
              <Eye className="text-orange-600 dark:text-orange-500 shrink-0 mt-1" size={22} />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 dark:text-white mb-1">{t.about.vision}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{about.vision}</p>
              </div>
            </div>
          </div>
        </motion.div>
        <motion.div {...fadeUp} className="relative">
          <img
            src={fileUrl(image) || ABOUT_DEFAULT}
            alt="Tanjung Marina Global"
            className="rounded-xl border border-slate-200 dark:border-white/10 w-full object-cover aspect-[4/3]"
          />
          <div className="absolute -bottom-6 -left-6 bg-orange-600 rounded-xl p-6 hidden md:block">
            <p className="font-heading font-extrabold text-white text-3xl">2022</p>
            <p className="text-orange-100 text-sm">{t.about.founded}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const SERVICE_ICONS = [Home, Building2, Factory, Snowflake, Package, Container];

const waQuoteLink = (contact, serviceName) => {
  const raw = (contact?.whatsapp || "").trim();
  let digits = raw.replace(/[^0-9]/g, "");
  if (/^https?:\/\//i.test(raw) || raw.toLowerCase().includes("wa.me/message") || !digits) {
    digits = (contact?.phone || "").replace(/[^0-9]/g, "");
  }
  if (!digits) return null;
  if (digits.startsWith("0")) digits = "6" + digits;
  const text = encodeURIComponent(`Salam, saya berminat untuk mendapatkan sebut harga bagi perkhidmatan: ${serviceName}. Boleh saya dapatkan maklumat lanjut?`);
  return `https://wa.me/${digits}?text=${text}`;
};

export const Services = ({ services, contact }) => {
  const { t } = usePrefs();
  return (
    <section id="perkhidmatan" data-testid="services-section" className="py-24 lg:py-32 bg-white dark:bg-[#0b1533] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-20">
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.services.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-5xl mt-3 leading-tight">{t.services.heading}</h2>
        </motion.div>
        <div className="space-y-24 lg:space-y-32">
          {services.map((s, i) => {
            const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
            const isReverse = i % 2 === 1;
            const rotate = isReverse ? "lg:rotate-[1.5deg]" : "lg:-rotate-[1.5deg]";
            return (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7 }}
                data-testid={`service-card-${i}`}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isReverse ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className={`relative ${rotate} transition-transform duration-700 hover:rotate-0`}>
                  <div className="absolute -inset-3 bg-gradient-to-br from-orange-500/20 via-transparent to-sky-500/15 rounded-3xl blur-xl" />
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 aspect-[4/3]">
                    {s.video ? (
                      <video src={fileUrl(s.video)} className="w-full h-full object-cover" autoPlay muted loop playsInline data-testid={`service-video-${i}`} />
                    ) : s.image ? (
                      <img src={fileUrl(s.image)} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-900 to-[#0b1533] flex items-center justify-center">
                        <Icon size={80} className="text-orange-500/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  </div>
                </div>
                <div>
                  <div className="w-14 h-14 rounded-full bg-orange-600/15 dark:bg-orange-600/20 border border-orange-500/30 flex items-center justify-center mb-5">
                    <Icon className="text-orange-600 dark:text-orange-400" size={24} />
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 dark:text-white text-2xl lg:text-3xl mb-4 leading-tight">{s.name}</h3>
                  <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed mb-6">{s.desc}</p>
                  {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                    <ul className="space-y-2.5 mb-8">
                      {s.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-3 text-sm">
                          <span className="mt-1.5 shrink-0 w-4 h-4 rounded-full bg-orange-600/20 border border-orange-500/50 flex items-center justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                          </span>
                          <span className="text-slate-700 dark:text-slate-300">{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    {s.cta_text && s.cta_link && (
                      <Link
                        to={s.cta_link}
                        data-testid={`service-cta-${i}`}
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-orange-600/25 transition-all hover:-translate-y-0.5"
                      >
                        {s.cta_text}
                      </Link>
                    )}
                    {waQuoteLink(contact, s.name) && (
                      <a
                        href={waQuoteLink(contact, s.name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`service-whatsapp-${i}`}
                        onClick={trackConversion}
                        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold px-6 py-3 rounded-full shadow-lg shadow-green-600/25 transition-all hover:-translate-y-0.5"
                      >
                        <svg viewBox="0 0 32 32" className="w-5 h-5 fill-white" aria-hidden="true">
                          <path d="M16 3C9.373 3 4 8.373 4 15c0 2.31.655 4.467 1.789 6.297L4.06 27.94l6.816-1.694A11.94 11.94 0 0 0 16 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 21.818a9.77 9.77 0 0 1-4.98-1.362l-.357-.212-4.045 1.005 1.079-3.944-.233-.372A9.77 9.77 0 0 1 6.182 15c0-5.423 4.395-9.818 9.818-9.818S25.818 9.577 25.818 15 21.423 24.818 16 24.818zm5.384-7.348c-.295-.148-1.745-.861-2.015-.959-.27-.099-.467-.148-.664.148-.196.295-.762.958-.934 1.155-.172.197-.344.221-.639.074-.295-.148-1.246-.459-2.373-1.464-.877-.782-1.469-1.748-1.641-2.043-.172-.295-.018-.455.13-.602.132-.132.295-.344.443-.517.148-.172.196-.295.295-.492.098-.197.049-.369-.025-.517-.074-.148-.664-1.6-.909-2.19-.24-.576-.483-.498-.664-.507l-.566-.01c-.197 0-.516.074-.787.369-.27.295-1.032 1.008-1.032 2.46s1.057 2.853 1.204 3.05c.148.197 2.079 3.176 5.038 4.453.704.304 1.253.485 1.681.62.706.225 1.35.193 1.858.117.567-.085 1.745-.713 1.991-1.402.246-.688.246-1.278.172-1.401-.074-.123-.27-.197-.566-.345z" />
                        </svg>
                        WhatsApp
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Fleet = ({ fleet }) => {
  const { t } = usePrefs();
  return (
    <section id="armada" data-testid="fleet-section" className="py-24 lg:py-32 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <span className="text-orange-600 dark:text-orange-500 text-xs tracking-widest uppercase font-semibold">{t.fleet.badge}</span>
          <h2 className="font-heading font-bold text-slate-900 dark:text-white text-3xl lg:text-4xl mt-3">{t.fleet.heading}</h2>
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {fleet.map((f, i) => (
            <motion.div
              key={f.name}
              {...fadeUp}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              data-testid={`fleet-card-${i}`}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-xl p-6 text-center shadow-sm dark:shadow-none hover:border-orange-500/50 hover:-translate-y-1 transition-all"
            >
              <Truck className="text-orange-600 dark:text-orange-500 mx-auto mb-4" size={30} />
              <h3 className="font-heading font-semibold text-slate-900 dark:text-white text-sm mb-2">{f.name}</h3>
              <p className="text-slate-500 text-xs leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
        <motion.div {...fadeUp} className="mt-12 flex items-center gap-3 justify-center text-slate-600 dark:text-slate-400 text-sm">
          <ShieldCheck className="text-orange-600 dark:text-orange-500" size={18} />
          {t.fleet.safety}
        </motion.div>
      </div>
    </section>
  );
};
